import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { subDays, format } from 'date-fns'
import { IconDownload } from '@tabler/icons-react'
import moment from 'moment'
import { isEmpty } from 'lodash'

import { PageHeader } from '@/components/headers/PageHeader'
import { OrganizationFilter } from '@/components/filters/OrganizationFilter'
import { FilterForm } from '@/components/filters/FilterForm'
import { DateRangePickerReusable } from '@/components/datePickers/DateRangePickerReusable'
import {
  fetchFeatureFeedback,
  fetchFeatureFeedbackAggregate,
} from '@/reducers/featureFeedback/featureFeedback.actions'
import { getOrganizationOptions } from '@/utils/helpers'
import { parseToCsv } from '@/components/helpers/parseToCsv'
import { exportCsv } from '@/components/helpers/exportToCsv'
import { Button } from '@/components/buttons/Button'
import { FormField } from '@/components/forms/FormField'

import { FeatureFeedbackTable } from './FeatureFeedbackTable'
import { FeatureFeedbackCards } from './FeatureFeedbackCards'

const FeatureFeedbackPageComponent = ({ flags = {} }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { organizationid: currentUserOrgId } = useSelector((state) => state.currentUser)
  const isBaltoAdmin = currentUserOrgId === 1
  const canViewPage = isBaltoAdmin || !!flags.featureFeedbackEnabled
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(null)
  const { organizations, currentOrganization } = useSelector((state) => state)
  const organizationOptions = getOrganizationOptions(organizations)
  const {
    loading: { feedback: feedbackLoading },
    data: { feedback },
  } = useSelector((state) => state.featureFeedback)

  const defaultStartDate = format(subDays(new Date(), 6), "yyyy-MM-dd'T00:00:00'xxx")
  const defaultEndDate = format(new Date(), "yyyy-MM-dd'T23:59:59'xxx")
  const [dateRange, setDateRange] = useState({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
  })
  const modifyFeedbackNames = (row) => {
    if (row.feature === 'Active Coaching') {
      return { ...row, feature: 'AI Coaching' }
    }
    if (row.feature === 'Summary') {
      return { ...row, feature: 'Notes' }
    }

    return row
  }

  const transformedRows = feedback.map(modifyFeedbackNames).map((row) => ({
    ...row,
    question: row?.additional_info?.question,
    answer: row?.additional_info?.answer,
    ai_agent_guidance: row?.additional_info?.ai_agent_guidance,
    headline: row?.additional_info?.headline,
    content: row?.additional_info?.content,
    context: row?.additional_info?.context,
    checklist_item: row?.additional_info?.checklist_item,
    salesforce_data_included:
      row?.feature === 'Customer History'
        ? Boolean(row?.additional_info?.salesforce_data)
        : undefined,
    customer_highlights: (row?.additional_info?.notes?.formatted_summary ?? []).join(' • '),
    prior_call_ids: (row?.additional_info?.call_history ?? [])
      .map((call) => call?.call_id)
      .filter(Boolean),
  }))

  const formatDate = (date) => moment.utc(date).local().format('MMMM Do, YYYY')

  const fetchFeedbackWithParams = () => {
    const params = {}

    if (selectedOrganizationId) {
      params.requested_org_id = selectedOrganizationId
    }

    if (dateRange.startDate) {
      params.start_date = dateRange.startDate
    }

    if (dateRange.endDate) {
      params.end_date = dateRange.endDate
    }

    dispatch(fetchFeatureFeedback(params))
    dispatch(fetchFeatureFeedbackAggregate(params))
  }

  const handleOrganizationChange = (option) => {
    setSelectedOrganizationId(option?.value ?? null)
  }

  const handleDateRangeChange = ({ selection }) => {
    if (selection.startDate && selection.endDate) {
      const startDate = format(selection.startDate, "yyyy-MM-dd'T00:00:00'xxx")
      const endDate = format(selection.endDate, "yyyy-MM-dd'T23:59:59'xxx")

      setDateRange({ startDate, endDate })
    }
  }

  const handleDownload = () => {
    const csvColumns = [
      { accessor: 'call_id', label: 'Call ID' },
      { accessor: 'sentiment', label: 'Feedback' },
      { accessor: 'created_at', label: 'Created On' },
      { accessor: 'username', label: 'User' },
      { accessor: 'organization_name', label: 'Organization' },
      { accessor: 'feature', label: 'Feature' },
      { accessor: 'category', label: 'Category' },
      { accessor: 'comment', label: 'Comment' },
      { accessor: 'question', label: 'Question' },
      { accessor: 'answer', label: 'Answer' },
      { accessor: 'ai_agent_guidance', label: 'AI Agent Guidance' },
      { accessor: 'headline', label: 'Summary Headline' },
      { accessor: 'content', label: 'Summary Content' },
      { accessor: 'context', label: 'Playbook Context' },
      { accessor: 'checklist_item', label: 'Checklist Item' },
      { accessor: 'salesforce_data_included', label: 'Salesforce Data' },
      { accessor: 'customer_highlights', label: 'Customer Highlights' },
      { accessor: 'prior_call_ids', label: 'Prior Call IDs' },
    ]

    const csvRows = transformedRows.map((row) => ({
      ...row,
      sentiment: row.sentiment === true ? 'Positive' : row.sentiment === false ? 'Negative' : 'N/A',
      salesforce_data_included:
        row.salesforce_data_included === true
          ? 'Included'
          : row.salesforce_data_included === false
            ? 'Not Included'
            : '',
      prior_call_ids: (row.prior_call_ids ?? []).join('; '),
    }))

    const filterBy = {
      startDate: dateRange?.startDate ? moment(dateRange.startDate).format('YYYY-MM-DD') : '',
      endDate: dateRange?.endDate ? moment(dateRange.endDate).format('YYYY-MM-DD') : '',
    }

    if (!isEmpty(csvRows)) {
      const csvString = parseToCsv({ data: csvRows, headers: csvColumns })
      exportCsv(csvString, filterBy, 'Feature Feedback')
    }
  }

  useEffect(() => {
    if (isEmpty(flags)) {
      return
    }

    if (!canViewPage) {
      history.push('/')
    }
  }, [flags, canViewPage])

  useEffect(() => {
    if (!canViewPage) {
      return
    }

    fetchFeedbackWithParams()
  }, [selectedOrganizationId, dateRange, canViewPage])

  return (
    <div data-testid="feature-feedback-page">
      <PageHeader
        title="Feature Feedback"
        actions={
          <Button
            square
            onClick={handleDownload}
            tooltip="Download CSV"
            data-testid="csv-download-button"
          >
            <IconDownload />
          </Button>
        }
      />
      <FilterForm small sticky={false}>
        {isBaltoAdmin && (
          <OrganizationFilter
            value={selectedOrganizationId}
            options={organizationOptions}
            onChange={handleOrganizationChange}
            placeholder="All organizations"
            aria-label="Organization"
            isClearable
          />
        )}
        <FormField>
          <label>Date Range</label>
          <DateRangePickerReusable
            data-testid="date-range-picker"
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={handleDateRangeChange}
            friendlyLabel
            basicLabel
            saveOrCloseOnClickPreset
          />
        </FormField>
      </FilterForm>
      <div>
        <p className="mt mb">
          Viewing data for{' '}
          {isBaltoAdmin
            ? organizations.find((org) => org.id === selectedOrganizationId)?.name ||
              'all organizations'
            : currentOrganization.name}{' '}
          from {formatDate(dateRange.startDate)} to {formatDate(dateRange.endDate)}
        </p>
      </div>
      <FeatureFeedbackCards />
      <FeatureFeedbackTable loading={feedbackLoading} rows={transformedRows} />
    </div>
  )
}

export default withLDConsumer()(FeatureFeedbackPageComponent)
